import { useEffect, useState } from 'react';
import { useApiFetch } from '../api';
import type { ApiFetchFn } from '../api';
import type { TemplateTokenCatalog } from '../types/models/TemplateTokenCatalog';
import type { ApiResponseTemplateTokens } from '../types/api/ApiResponseAdminProduct';

interface UseTemplateTokensReturn {
  /** The catalog, or null while it is loading / after a failure (callers then show a static hint). */
  catalog: TemplateTokenCatalog | null;
  loading: boolean;
  /** Human-readable error message (the backend's JSend `message`), or null. */
  error: string | null;
}

// GET /admin/products/template-tokens serves DescriptionRenderer::TOKEN_CATALOG verbatim — it is a
// constant of the deployed backend, identical for every staff user and unchanged for the whole page
// load. So it is cached at MODULE level rather than per component: the invoice-description help
// mounts once per product modal, and re-fetching it on every popover open would be pure waste.
//
// `catalogPromise` also dedupes concurrent callers (two modals opening at once share one request).
// A failure clears it (and is never written to `catalogCache`), so the next mount of the help — i.e.
// the next time a product modal is opened — retries: a transient error must not poison the help for
// the rest of the session.
let catalogCache: TemplateTokenCatalog | null = null;
let catalogPromise: Promise<TemplateTokenCatalog> | null = null;

async function fetchCatalog(apiFetch: ApiFetchFn): Promise<TemplateTokenCatalog> {
  const response = await apiFetch('/admin/products/template-tokens');
  // Branch on the JSend `body.status`, NEVER `response.ok`: the backend maps controller exceptions
  // to `status: 'error'` at HTTP 200, so `response.ok` can be true on a failure.
  const body = (await response.json().catch(() => null)) as ApiResponseTemplateTokens | null;
  if (body?.status === 'success' && body.data?.template_tokens) {
    return body.data.template_tokens;
  }
  throw new Error(body?.message ?? 'Failed to load the template token catalog');
}

/**
 * Reads the `{{token}}` catalog for product description templates.
 *
 * `enabled` defaults to false so the request is opt-in and LAZY: the caller flips it on when the
 * help popover is first opened, which keeps the product create/edit form free of a fetch nobody
 * asked for (and, on failure, free of any blocking error state — the caller falls back to a static
 * hint). Once fetched, the module cache makes every later open instant.
 */
export const useTemplateTokens = (enabled = false): UseTemplateTokensReturn => {
  const [catalog, setCatalog] = useState<TemplateTokenCatalog | null>(catalogCache);
  const [error, setError] = useState<string | null>(null);
  const apiFetch = useApiFetch();

  useEffect(() => {
    if (!enabled || catalog) {
      return;
    }
    let cancelled = false;
    setError(null);
    // apiFetch gets a NEW identity on every OIDC silent renew, so this effect can re-run mid-flight;
    // reusing the pending promise keeps that from firing a second request.
    catalogPromise = catalogPromise ?? fetchCatalog(apiFetch);
    catalogPromise
      .then((loaded) => {
        catalogCache = loaded;
        if (!cancelled) {
          setCatalog(loaded);
        }
      })
      .catch((err: unknown) => {
        catalogPromise = null;
        if (!cancelled) {
          setError(err instanceof Error ? err.message : 'An error occurred while fetching the token catalog');
        }
      });
    return () => {
      cancelled = true;
    };
  }, [enabled, catalog, apiFetch]);

  // Derived, not state: the very render that enables the fetch already reports `loading`, so the
  // caller never flashes its error/fallback branch for one frame before the request starts.
  const loading = enabled && catalog === null && error === null;

  return { catalog, loading, error };
};
